import { useMemo, useRef, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import {
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
} from '@/components/ui/table';
import {
  Upload, ExternalLink, Pencil, Trash2, FileText, Link2, X,
} from 'lucide-react';
import { toast } from 'sonner';
import { getFurnitureImageUrl } from '@/lib/api';
import { ReceiptViewerPanel } from '@/components/furniture/ReceiptViewerPanel';

function fmtCurrency(val) {
  if (val == null || val === '') return '';
  const num = Number(val);
  if (Number.isNaN(num)) return '';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
}

function fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

// Resolve an item_id → "Room · Item" label. Rooms are flattened once.
function buildItemIndex(rooms) {
  const idx = {};
  for (const r of rooms || []) {
    for (const it of r.items || []) {
      idx[it.id] = { roomId: r.id, roomName: r.name, itemName: it.name };
    }
  }
  return idx;
}

// ─── Dialog: pick items across rooms that this receipt covers ───
function LinkItemsDialog({ open, onOpenChange, rooms, initial = [], onSave }) {
  const [selected, setSelected] = useState(new Set(initial));
  const [query, setQuery] = useState('');

  // Reset selection whenever the dialog reopens with new initial state
  // so switching between receipts doesn't carry over the previous picks.
  const prevKey = useRef('');
  const key = `${open}|${initial.join(',')}`;
  if (key !== prevKey.current) {
    prevKey.current = key;
    if (open) setSelected(new Set(initial));
  }

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (rooms || []).map(room => ({
      ...room,
      _items: (room.items || []).filter(i => {
        if (!q) return true;
        return (
          (i.name || '').toLowerCase().includes(q) ||
          (room.name || '').toLowerCase().includes(q)
        );
      }),
    })).filter(r => r._items.length > 0);
  }, [rooms, query]);

  const save = () => {
    onSave(Array.from(selected));
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="link-items-dialog">
        <DialogHeader>
          <DialogTitle>Link receipt to items</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <Input
            placeholder="Search rooms or items..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            data-testid="link-items-search"
          />
          <div className="max-h-[400px] overflow-y-auto border rounded-md divide-y">
            {filtered.length === 0 && (
              <p className="text-sm text-[#5b6773] text-center py-6">No matching items.</p>
            )}
            {filtered.map(room => (
              <div key={room.id} className="p-2">
                <p className="text-[10px] uppercase tracking-wider text-[#5b6773] font-semibold px-1 mb-1">{room.name}</p>
                <div className="space-y-1">
                  {room._items.map(it => (
                    <label
                      key={it.id}
                      className="flex items-center gap-2 px-2 py-1 rounded hover:bg-[#2d948c]/5 cursor-pointer"
                      data-testid={`link-item-row-${it.id}`}
                    >
                      <Checkbox
                        checked={selected.has(it.id)}
                        onCheckedChange={() => toggle(it.id)}
                      />
                      <span className="text-sm text-[#1f2933] flex-1 truncate">{it.name}</span>
                      {it.is_essential && (
                        <span className="text-[9px] uppercase tracking-wider text-[#2d948c]">essential</span>
                      )}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <p className="text-xs text-[#5b6773]">
            {selected.size} item{selected.size === 1 ? '' : 's'} selected
          </p>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="link-items-cancel">Cancel</Button>
          <Button onClick={save} data-testid="link-items-save">Save links</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Upload dialog ─────────────────────────────────────────────
function UploadReceiptDialog({ open, onOpenChange, rooms, onUpload }) {
  const [file, setFile] = useState(null);
  const [vendor, setVendor] = useState('');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [itemIds, setItemIds] = useState([]);
  const [showLink, setShowLink] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const reset = () => {
    setFile(null); setVendor(''); setAmount(''); setNote(''); setItemIds([]); setSubmitting(false);
    if (fileRef.current) fileRef.current.value = '';
  };

  const submit = async () => {
    if (!file) { toast.error('Pick a file first'); return; }
    setSubmitting(true);
    const res = await onUpload({ file, itemIds, vendor, amount, note });
    setSubmitting(false);
    if (res) {
      reset();
      onOpenChange(false);
    }
  };

  const itemIdx = buildItemIndex(rooms);

  return (
    <>
      <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
        <DialogContent className="max-w-lg" data-testid="upload-receipt-dialog">
          <DialogHeader>
            <DialogTitle>Upload receipt</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#5b6773] font-semibold">File *</label>
              <Input
                ref={fileRef}
                type="file"
                accept="image/*,application/pdf"
                onChange={e => setFile(e.target.files?.[0] || null)}
                data-testid="upload-receipt-file"
              />
              {file && <p className="text-xs text-[#5b6773] mt-1 truncate">{file.name}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-[#5b6773] font-semibold">Vendor</label>
                <Input value={vendor} onChange={e => setVendor(e.target.value)} placeholder="West Elm..." data-testid="upload-receipt-vendor" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-[#5b6773] font-semibold">Amount</label>
                <Input value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" data-testid="upload-receipt-amount" />
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#5b6773] font-semibold">Note</label>
              <Input value={note} onChange={e => setNote(e.target.value)} placeholder="Optional..." data-testid="upload-receipt-note" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-[11px] uppercase tracking-wider text-[#5b6773] font-semibold">Linked items</label>
                <Button
                  variant="outline" size="sm" className="h-7 gap-1"
                  onClick={() => setShowLink(true)}
                  data-testid="upload-receipt-link-btn"
                >
                  <Link2 className="h-3 w-3" /> {itemIds.length > 0 ? 'Edit' : 'Link items'}
                </Button>
              </div>
              {itemIds.length === 0 ? (
                <p className="text-xs text-[#5b6773] mt-1">No items linked yet. You can link later too.</p>
              ) : (
                <div className="flex flex-wrap gap-1 mt-2">
                  {itemIds.map(id => {
                    const info = itemIdx[id];
                    if (!info) return null;
                    return (
                      <span key={id} className="inline-flex items-center gap-1 text-[11px] bg-[#2d948c]/10 text-[#2d948c] px-2 py-0.5 rounded-full">
                        {info.roomName} · {info.itemName}
                        <button onClick={() => setItemIds(ids => ids.filter(x => x !== id))}>
                          <X className="h-2.5 w-2.5" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => { reset(); onOpenChange(false); }} data-testid="upload-receipt-cancel">Cancel</Button>
            <Button onClick={submit} disabled={submitting || !file} data-testid="upload-receipt-submit">
              {submitting ? 'Uploading...' : 'Upload'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <LinkItemsDialog
        open={showLink}
        onOpenChange={setShowLink}
        rooms={rooms}
        initial={itemIds}
        onSave={setItemIds}
      />
    </>
  );
}

// ─── Main list component ───────────────────────────────────────
export const ReceiptsView = ({ rooms, receipts, readOnly, onUpload, onUpdate, onDelete }) => {
  const [showUpload, setShowUpload] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [viewingId, setViewingId] = useState(null);

  const itemIdx = useMemo(() => buildItemIndex(rooms), [rooms]);

  const editing = receipts.find(r => r.id === editingId) || null;
  const viewing = receipts.find(r => r.id === viewingId) || null;

  const total = receipts.reduce((s, r) => s + (Number(r.amount) || 0), 0);

  return (
    <Card className="shadow-[0_1px_0_rgba(16,24,40,0.04),0_10px_30px_rgba(16,24,40,0.06)]">
      <CardContent className="p-0">
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <div>
            <h3 className="font-heading font-semibold text-[#1f2933]">Receipts</h3>
            <p className="text-xs text-[#5b6773]">
              {receipts.length} file{receipts.length === 1 ? '' : 's'}
              {total > 0 ? ` · ${fmtCurrency(total)} total` : ''}
            </p>
          </div>
          {!readOnly && (
            <Button size="sm" className="gap-1.5" onClick={() => setShowUpload(true)} data-testid="open-upload-receipt">
              <Upload className="h-3.5 w-3.5" /> Upload receipt
            </Button>
          )}
        </div>

        {receipts.length === 0 ? (
          <div className="py-14 text-center">
            <FileText className="h-10 w-10 text-[#5b6773]/40 mx-auto mb-2" />
            <p className="text-sm text-[#5b6773]">No receipts yet.</p>
            {!readOnly && (
              <p className="text-xs text-[#5b6773] mt-1">
                Upload from here, or use the Receipt button next to each item.
              </p>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#2d948c]/[0.07]">
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-[#1f2933]">File</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-[#1f2933]">Vendor</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-[#1f2933] text-right">Amount</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-[#1f2933]">Linked Items</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-[#1f2933]">Note</TableHead>
                  <TableHead className="text-[10px] uppercase tracking-wider font-semibold text-[#1f2933]">Uploaded</TableHead>
                  {!readOnly && <TableHead className="w-20" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {receipts.map(r => (
                  <TableRow key={r.id} className="group hover:bg-[#2d948c]/[0.03]" data-testid={`receipt-row-${r.id}`}>
                    <TableCell className="py-2">
                      <button
                        type="button"
                        onClick={() => setViewingId(r.id)}
                        className="inline-flex items-center gap-1 text-[#2f6f8f] hover:underline text-sm text-left"
                        data-testid={`view-receipt-${r.id}`}
                      >
                        <FileText className="h-3.5 w-3.5" />
                        <span className="truncate max-w-[180px]">{r.file_name || 'receipt'}</span>
                        <ExternalLink className="h-3 w-3 opacity-60" />
                      </button>
                    </TableCell>
                    <TableCell className="py-2 text-sm text-[#1f2933]">{r.vendor || '—'}</TableCell>
                    <TableCell className="py-2 text-sm tabular-nums text-right text-[#1f2933]">
                      {r.amount != null ? fmtCurrency(r.amount) : '—'}
                    </TableCell>
                    <TableCell className="py-2 text-xs text-[#5b6773]">
                      {(r.item_ids || []).length === 0 ? (
                        <span className="italic">— not linked —</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {(r.item_ids || []).map(id => {
                            const info = itemIdx[id];
                            // For auto-committed (via receipt processing) the
                            // per-item amount is available in line_item_summary.
                            const summary = (r.line_item_summary || []).find(s => s.item_id === id);
                            if (!info) return (
                              <span key={id} className="text-[11px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                                (deleted)
                              </span>
                            );
                            return (
                              <span key={id} className="text-[11px] bg-[#2d948c]/10 text-[#2d948c] px-2 py-0.5 rounded-full tabular-nums">
                                {info.roomName} · {info.itemName}
                                {summary?.amount != null && <span className="ml-1 font-semibold">· ${Math.round(Number(summary.amount)).toLocaleString('en-US')}</span>}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="py-2 text-xs text-[#5b6773] max-w-[180px] truncate">{r.note || '—'}</TableCell>
                    <TableCell className="py-2 text-xs text-[#5b6773]">{fmtDate(r.uploaded_at)}</TableCell>
                    {!readOnly && (
                      <TableCell className="py-2">
                        <div className="flex items-center gap-0.5">
                          <Button
                            variant="ghost" size="icon" className="h-7 w-7"
                            onClick={() => setEditingId(r.id)}
                            title="Edit"
                            data-testid={`edit-receipt-${r.id}`}
                          >
                            <Pencil className="h-3 w-3" />
                          </Button>
                          {confirmDeleteId === r.id ? (
                            <div className="flex items-center gap-0.5">
                              <Button
                                variant="destructive" size="sm" className="h-7 text-[11px] px-2"
                                onClick={() => { onDelete(r.id); setConfirmDeleteId(null); }}
                                data-testid={`confirm-delete-receipt-${r.id}`}
                              >
                                Delete
                              </Button>
                              <Button
                                variant="ghost" size="sm" className="h-7 text-[11px] px-2"
                                onClick={() => setConfirmDeleteId(null)}
                                data-testid={`cancel-delete-receipt-${r.id}`}
                              >
                                No
                              </Button>
                            </div>
                          ) : (
                            <Button
                              variant="ghost" size="icon" className="h-7 w-7 text-destructive"
                              onClick={() => setConfirmDeleteId(r.id)}
                              title="Delete"
                              data-testid={`delete-receipt-${r.id}`}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      <ReceiptViewerPanel
        open={!!viewing}
        onClose={() => setViewingId(null)}
        receipt={viewing}
        title="Project receipt"
        extras={viewing && (viewing.line_item_summary || []).length > 0 ? (
          <div className="space-y-2 text-xs">
            <p className="text-[10px] uppercase tracking-wider text-[#5b6773] font-semibold">Items charged</p>
            {(viewing.line_item_summary || []).map((s, i) => {
              const info = itemIdx[s.item_id];
              return (
                <div key={s.item_id || i} className="border rounded px-2 py-1.5">
                  <div className="flex items-center justify-between gap-2">
                    <span className="truncate text-[#1f2933] font-medium">
                      {info ? `${info.roomName} · ${info.itemName}` : '(unknown item)'}
                    </span>
                    <span className="tabular-nums font-semibold text-[#2d948c]">{fmtCurrency(s.amount)}</span>
                  </div>
                  {(s.lines || []).map((ln, j) => (
                    <div key={j} className="flex items-center justify-between text-[11px] text-[#5b6773] mt-0.5">
                      <span className="truncate pr-2">{ln.line_item_name}{ln.count > 1 ? ` × ${ln.count}` : ''}</span>
                      <span className="tabular-nums">{fmtCurrency(ln.cost)}</span>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        ) : null}
      />

      <UploadReceiptDialog
        open={showUpload}
        onOpenChange={setShowUpload}
        rooms={rooms}
        onUpload={onUpload}
      />

      {editing && (
        <EditReceiptDialog
          open={!!editing}
          onOpenChange={(v) => { if (!v) setEditingId(null); }}
          receipt={editing}
          rooms={rooms}
          onSave={(data) => { onUpdate(editing.id, data); setEditingId(null); }}
        />
      )}
    </Card>
  );
};

// ─── Edit receipt metadata + linked items ──────────────────────
function EditReceiptDialog({ open, onOpenChange, receipt, rooms, onSave }) {
  const [vendor, setVendor] = useState(receipt.vendor || '');
  const [amount, setAmount] = useState(receipt.amount == null ? '' : String(receipt.amount));
  const [note, setNote] = useState(receipt.note || '');
  const [itemIds, setItemIds] = useState(receipt.item_ids || []);
  const [showLink, setShowLink] = useState(false);

  const itemIdx = buildItemIndex(rooms);

  const save = () => {
    onSave({
      vendor, note, amount: amount === '' ? null : amount,
      item_ids: itemIds,
    });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-lg" data-testid="edit-receipt-dialog">
          <DialogHeader>
            <DialogTitle>Edit receipt</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-[#5b6773] font-semibold">Vendor</label>
                <Input value={vendor} onChange={e => setVendor(e.target.value)} data-testid="edit-receipt-vendor" />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-[#5b6773] font-semibold">Amount</label>
                <Input value={amount} onChange={e => setAmount(e.target.value)} data-testid="edit-receipt-amount" />
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-[#5b6773] font-semibold">Note</label>
              <Input value={note} onChange={e => setNote(e.target.value)} data-testid="edit-receipt-note" />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <label className="text-[11px] uppercase tracking-wider text-[#5b6773] font-semibold">Linked items</label>
                <Button
                  variant="outline" size="sm" className="h-7 gap-1"
                  onClick={() => setShowLink(true)}
                  data-testid="edit-receipt-link-btn"
                >
                  <Link2 className="h-3 w-3" /> Edit links
                </Button>
              </div>
              {itemIds.length === 0 ? (
                <p className="text-xs text-[#5b6773] mt-1">Not linked to any item.</p>
              ) : (
                <div className="flex flex-wrap gap-1 mt-2">
                  {itemIds.map(id => {
                    const info = itemIdx[id];
                    if (!info) return (
                      <span key={id} className="inline-flex items-center gap-1 text-[11px] bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                        (deleted)
                        <button onClick={() => setItemIds(ids => ids.filter(x => x !== id))}>
                          <X className="h-2.5 w-2.5" />
                        </button>
                      </span>
                    );
                    return (
                      <span key={id} className="inline-flex items-center gap-1 text-[11px] bg-[#2d948c]/10 text-[#2d948c] px-2 py-0.5 rounded-full">
                        {info.roomName} · {info.itemName}
                        <button onClick={() => setItemIds(ids => ids.filter(x => x !== id))}>
                          <X className="h-2.5 w-2.5" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="edit-receipt-cancel">Cancel</Button>
            <Button onClick={save} data-testid="edit-receipt-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <LinkItemsDialog
        open={showLink}
        onOpenChange={setShowLink}
        rooms={rooms}
        initial={itemIds}
        onSave={setItemIds}
      />
    </>
  );
}
